import { ApiError } from "./types";
import { getActiveEnvSlug } from "./active-env";

type Method = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

export interface RequestOptions {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Low-level fetch wrapper for the dashboard API. All requests carry the
 * session cookie via `credentials: "include"`. Non-2xx responses throw
 * `ApiError` with the parsed JSON body (or an empty object if body is
 * missing/malformed).
 */
export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, query } = opts;

  const url = new URL(path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  // Scope every request to the selected environment (falls back to Production
  // server-side when absent). Multi-environment seam.
  const env = getActiveEnvSlug();
  if (env) headers["X-Deleggate-Env"] = env;

  const init: RequestInit = {
    method,
    credentials: "include",
    headers,
    signal,
  };
  if (body !== undefined) {
    (init.headers as Record<string, string>)["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url.pathname + url.search, init);

  if (res.status === 204) {
    // No content — return undefined cast; callers should type as void.
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, `malformed JSON response`, text);
    }
  }

  if (!res.ok) {
    const msg = (parsed as { error?: string } | null)?.error ?? res.statusText;
    throw new ApiError(res.status, msg, parsed);
  }

  return parsed as T;
}
